import type {
  FieldValidationIssue,
  StructuredDocumentRecord,
  StructuredLineItemRecord,
} from '@/features/case-workspace/types'
import {
  buildCbp7501FieldStateEngine,
  isManifestQuantityEnabledForLine,
  requiresChargesForRecord,
  requiresManifestQuantityForRecord,
} from '@/features/case-workspace/cbp7501FieldState'
import { deriveCbp7501LineAutomation } from '@/features/case-workspace/cbp7501LineAutomation'

export const CBP_FORM_7501_VALIDATION_SCHEMA_ID = 'cbp-form-7501-validation-schema.json'

const TOP_LEVEL_PATTERNS = {
  entryNumber: /^[A-Z0-9]{3}[0-9]{7}[0-9]$/,
  entryType: /^(01|02|03|04|05|06|07|08|11|12|21|22|23|24|25|26|31|32|34|38|51|52|61|62|63)(\/(ABI|LIVE|L|ABI-[ASPN]|ABI\/[ASPN]))?$/,
  suretyNumber: /^[0-9]{3}$/,
  portCode: /^[0-9]{4}$/,
  countryOfOrigin: /^([A-Z]{2}|MULTI)$/,
  billOfLadingOrAirWaybillNumber: /^[A-Z0-9]{1,16}$/,
  manufacturerId: /^([A-Z0-9]{2,15}|MULTI)$/,
  exportingCountry: /^([A-Z]{2}|MULTI)$/,
  foreignPortOfLading: /^([0-9]{5}|MULTI)$/,
  usPortOfUnlading: /^[0-9]{4}$/,
  metalCountry: /^([A-Z]{2}|OTH|N\/A)$/,
  partyNumber: /^(SAME|00-0000000|[0-9]{2}-[0-9]{7}([A-Z0-9]{2})?|[0-9]{3}-[0-9]{2}-[0-9]{4}|[0-9]{6}-[0-9]{5})$/,
  state: /^[A-Z]{2}$/,
  zip: /^[0-9]{5}(-[0-9]{4})?$/,
  lineNumber: /^[0-9]{3}$/,
  htsusNumber: /^[0-9]{4}\.[0-9]{2}\.[0-9]{4}$/,
  adCvdCaseNumber: /^[AC]-?[0-9]{3}-[0-9]{3}-[0-9]{3}$/,
  visaNumber: /^[VC][0-9][A-Z]{2}[A-Z0-9]{6}$/,
  netQuantity: /^\d+(\.\d+)?\s+\S.+$/,
} as const

const BOND_TYPES = new Set(['0', '8', '9'])
const MODE_OF_TRANSPORT = new Set(['10', '11', '12', '20', '21', '30', '31', '32', '33', '34', '40', '41', '50', '60', '70'])
const MISSING_DOCUMENT_CODES = new Set(['01', '10', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '26', '98', '99'])
const RELATIONSHIP_VALUES = new Set(['Y', 'N', 'RELATED', 'NOT RELATED'])
const DECLARANT_TYPES = new Set(['importer_of_record', 'owner', 'purchaser', 'authorized_agent'])
const IRC_NOT_APPLICABLE_MESSAGE = 'Not Applicable to HTSUS code'

function fieldValue(record: StructuredDocumentRecord, key: string) {
  return record.fields[key]?.value?.trim() ?? ''
}

function lineFieldValue(lineItem: StructuredLineItemRecord, key: string) {
  return lineItem.fields[key]?.value?.trim() ?? ''
}

function lineRuntimeValue(record: StructuredDocumentRecord, lineItem: StructuredLineItemRecord, key: string) {
  if (key === 'relationship') {
    return lineFieldValue(lineItem, key) || fieldValue(record, 'relationship_global')
  }
  return lineFieldValue(lineItem, key)
}

function parseUsDate(value: string) {
  const match = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/)
  if (!match) return null
  const [, month, day, year] = match
  const date = new Date(`${year}-${month}-${day}T00:00:00Z`)
  if (Number.isNaN(date.getTime())) return null
  // Reject calendar rollover (e.g. 02/30/2026 → Mar 2) by confirming the
  // parsed components match the input.
  if (
    date.getUTCFullYear() !== Number(year)
    || date.getUTCMonth() + 1 !== Number(month)
    || date.getUTCDate() !== Number(day)
  ) {
    return null
  }
  return date
}

function isNonNegativeNumber(value: string) {
  if (!/^\d+(\.\d+)?$/.test(value)) return false
  return Number(value) >= 0
}

function isNonNegativeInteger(value: string) {
  if (!/^\d+$/.test(value)) return false
  return Number(value) >= 0
}

function isPositiveNumber(value: string) {
  if (!/^\d+(\.\d+)?$/.test(value)) return false
  return Number(value) > 0
}

function addIssue(
  issues: FieldValidationIssue[],
  fieldKey: string,
  message: string,
  severity: FieldValidationIssue['severity'] = 'error',
) {
  issues.push({ fieldKey, message, severity })
}

function validateAddressField(issues: FieldValidationIssue[], fieldKey: string, address: string, label: string) {
  if (!address) {
    addIssue(issues, fieldKey, `${label} is required.`)
    return
  }

  const stateMatch = address.match(/\b([A-Z]{2})\b/)
  const zipMatch = address.match(/[0-9]{5}(?:-[0-9]{4})?/)

  if (!stateMatch || !TOP_LEVEL_PATTERNS.state.test(stateMatch[1])) {
    addIssue(issues, fieldKey, `${label} should include a two-letter state or territory code.`, 'warning')
  }
  if (!zipMatch || !TOP_LEVEL_PATTERNS.zip.test(zipMatch[0])) {
    addIssue(issues, fieldKey, `${label} should include a valid ZIP code.`, 'warning')
  }
}

function hasSplitAddressValues(record: StructuredDocumentRecord, fieldKeys: string[]) {
  return fieldKeys.some((key) => fieldValue(record, key).length > 0)
}

function validateSplitAddressStateAndZip(
  issues: FieldValidationIssue[],
  {
    stateFieldKey,
    stateLabel,
    zipFieldKey,
    zipLabel,
  }: {
    stateFieldKey: string
    stateLabel: string
    zipFieldKey: string
    zipLabel: string
  },
  record: StructuredDocumentRecord,
) {
  const stateValue = fieldValue(record, stateFieldKey)
  const zipValue = fieldValue(record, zipFieldKey)

  if (stateValue && !TOP_LEVEL_PATTERNS.state.test(stateValue)) {
    addIssue(issues, stateFieldKey, `${stateLabel} should use a two-letter state or territory code.`, 'warning')
  }

  if (zipValue && !TOP_LEVEL_PATTERNS.zip.test(zipValue)) {
    addIssue(issues, zipFieldKey, `${zipLabel} should include a valid ZIP code.`, 'warning')
  }
}

function validateMissingDocuments(issues: FieldValidationIssue[], value: string) {
  if (!value) return
  const codes = value
    .split(',')
    .map((code) => code.trim())
    .filter(Boolean)

  if (codes.length > 2) {
    addIssue(issues, 'missing_docs', 'Use no more than two missing document codes.')
    return
  }

  if (!codes.every((code) => MISSING_DOCUMENT_CODES.has(code))) {
    addIssue(issues, 'missing_docs', 'Use valid CBP missing document codes only.')
  }

  if (codes.includes('99') && codes.length !== 2) {
    addIssue(issues, 'missing_docs', 'Code 99 must be paired with one other missing document code.')
  }
}

function validateLineItem(
  issues: FieldValidationIssue[],
  record: StructuredDocumentRecord,
  lineItem: StructuredLineItemRecord,
) {
  const automation = deriveCbp7501LineAutomation(lineItem)
  const lineKey = (key: string) => `${key}:${lineItem.id}`
  const lineNumber = lineFieldValue(lineItem, 'line_number')
  const description = lineFieldValue(lineItem, 'description_of_merchandise')
  const htsusNumber = lineFieldValue(lineItem, 'htsus_number')
  const adCvdApplies = lineFieldValue(lineItem, 'ad_cvd_applies') === 'true'
  const adCvdNumber = lineFieldValue(lineItem, 'ad_cvd_number')
  const grossWeight = lineFieldValue(lineItem, 'gross_weight')
  const grossWeightUnit = lineFieldValue(lineItem, 'gross_weight_unit')
  const manifestQuantity = lineFieldValue(lineItem, 'manifest_quantity')
  const manifestQuantityUnit = lineFieldValue(lineItem, 'manifest_quantity_unit')
  const netQuantity = lineFieldValue(lineItem, 'net_quantity_htsus_units')
  const enteredValue = lineFieldValue(lineItem, 'entered_value')
  const charges = lineFieldValue(lineItem, 'charges')
  const globalRelationship = fieldValue(record, 'relationship_global')
  const relationship = (lineFieldValue(lineItem, 'relationship') || globalRelationship).toUpperCase()
  const storedHtsusRate = lineFieldValue(lineItem, 'htsus_rate')
  const storedAdCvdRate = lineFieldValue(lineItem, 'ad_cvd_rate')
  const storedIrcRate = lineFieldValue(lineItem, 'irc_rate')
  const htsusRate = storedHtsusRate || automation.htsusRate.trim()
  const adCvdRate = storedAdCvdRate || automation.adCvdRate.trim()
  const ircRate = storedIrcRate || automation.ircRate.trim()
  const visaNumber = lineFieldValue(lineItem, 'visa_certificate_number')
  const dutyAndTax = automation.dutyAndIrTax.trim()
  const adCvdRateRequired = adCvdApplies || Boolean(adCvdNumber)
  const visaRequired = automation.flags.includes('QUOTA_OR_VISA')
  const ircRateRequired =
    automation.flags.includes('IRC_TAX_APPLICABLE')
    && ircRate !== IRC_NOT_APPLICABLE_MESSAGE
  const manifestQuantityRequired = requiresManifestQuantityForRecord(record)
  const manifestQuantityEnabled = isManifestQuantityEnabledForLine(record, lineItem)
  const chargesRequired = requiresChargesForRecord(record)
  const visaUsedAsRate =
    TOP_LEVEL_PATTERNS.visaNumber.test(storedHtsusRate)
    || TOP_LEVEL_PATTERNS.visaNumber.test(storedAdCvdRate)
    || TOP_LEVEL_PATTERNS.visaNumber.test(storedIrcRate)

  if (!TOP_LEVEL_PATTERNS.lineNumber.test(lineNumber)) {
    addIssue(issues, lineKey('line_number'), 'Line number must be a three-digit sequence such as 001.')
  }
  if (description.length < 3) {
    addIssue(issues, lineKey('description_of_merchandise'), 'Merchandise description must be at least three characters long.')
  }
  if (!htsusNumber) {
    addIssue(issues, lineKey('htsus_number'), 'HTSUS No. is required for every line item.')
  } else if (!TOP_LEVEL_PATTERNS.htsusNumber.test(htsusNumber)) {
    addIssue(issues, lineKey('htsus_number'), 'HTSUS number must use the 0000.00.0000 format.')
  }
  if (adCvdApplies && !adCvdNumber) {
    addIssue(issues, lineKey('ad_cvd_number'), 'AD/CVD No. is required when AD/CVD applies.')
  } else if (adCvdNumber && !TOP_LEVEL_PATTERNS.adCvdCaseNumber.test(adCvdNumber)) {
    addIssue(issues, lineKey('ad_cvd_number'), 'AD/CVD number must use the A-000-000-000 or C-000-000-000 format.')
  }
  if (!isPositiveNumber(grossWeight)) {
    addIssue(issues, lineKey('gross_weight'), 'Gross weight must be a number greater than zero.')
  }
  if (!grossWeightUnit || grossWeightUnit.toLowerCase() !== 'kg') {
    addIssue(issues, lineKey('gross_weight_unit'), 'Gross Weight is always reported in kilograms (KG).')
  }
  if (manifestQuantityEnabled && manifestQuantityRequired && !manifestQuantity) {
    addIssue(issues, lineKey('manifest_quantity'), 'Manifest Quantity is required for this filing scenario.')
  } else if (manifestQuantityEnabled && manifestQuantity && !isNonNegativeNumber(manifestQuantity)) {
    addIssue(issues, lineKey('manifest_quantity'), 'Manifest quantity must be a non-negative number.', manifestQuantityRequired ? 'error' : 'warning')
  }
  if (manifestQuantityEnabled && manifestQuantityRequired && manifestQuantity && !manifestQuantityUnit) {
    addIssue(issues, lineKey('manifest_quantity_unit'), 'Manifest quantity must include a unit of measure.')
  } else if (manifestQuantityEnabled && !manifestQuantityRequired && manifestQuantity && !manifestQuantityUnit) {
    addIssue(issues, lineKey('manifest_quantity_unit'), 'Manifest quantity should include a unit of measure.', 'warning')
  }
  if (!TOP_LEVEL_PATTERNS.netQuantity.test(netQuantity)) {
    addIssue(issues, lineKey('net_quantity_htsus_units'), 'Net quantity must include both a quantity and a unit of measure.')
  }
  if (!isNonNegativeInteger(enteredValue)) {
    addIssue(issues, lineKey('entered_value'), 'Entered Value must be reported in whole U.S. dollars.')
  }
  if (chargesRequired && !charges) {
    addIssue(issues, lineKey('charges'), 'Charges / CHGS are required unless Mode of Transport is 60.')
  } else if (charges && !isNonNegativeInteger(charges)) {
    addIssue(issues, lineKey('charges'), 'Charges / CHGS must be reported in whole U.S. dollars.', chargesRequired ? 'error' : 'warning')
  }
  if (!RELATIONSHIP_VALUES.has(relationship)) {
    addIssue(issues, lineKey('relationship'), 'Relationship must be Y, N, related, or not related.')
  }
  if (!htsusRate) {
    addIssue(issues, lineKey('htsus_rate'), 'HTSUS Rate is required for this line item.')
  }
  if (adCvdRateRequired && !adCvdRate) {
    addIssue(issues, lineKey('ad_cvd_rate'), 'AD/CVD Rate is required when an AD/CVD No. is provided or AD/CVD applies.')
  }
  if (ircRateRequired && !ircRate) {
    addIssue(issues, lineKey('irc_rate'), 'IRC Rate is required when internal revenue or excise tax applies.')
  }
  if (visaRequired && !visaNumber) {
    addIssue(issues, lineKey('visa_certificate_number'), 'VISA Number is required when textile/quota/visa rules apply.')
  }
  if (visaNumber && !TOP_LEVEL_PATTERNS.visaNumber.test(visaNumber)) {
    addIssue(issues, lineKey('visa_certificate_number'), 'Visa or certificate number is not in a valid format.', 'warning')
  }
  if (visaUsedAsRate) {
    addIssue(issues, lineKey('visa_certificate_number'), 'VISA Number is a compliance identifier and cannot be used instead of a duty rate.')
  }
  if (!isNonNegativeNumber(dutyAndTax)) {
    addIssue(issues, lineKey('duty_and_ir_tax'), 'Duty and IR tax must be a non-negative amount.')
  }
}

export function validateCbp7501SchemaRules(record: StructuredDocumentRecord) {
  if (record.documentType !== 'cbp_7501') return []

  const issues: FieldValidationIssue[] = []
  const fieldStateEngine = buildCbp7501FieldStateEngine(record)
  const entryNumber = fieldValue(record, 'entry_number')
  const entryType = fieldValue(record, 'entry_type')
  const summaryDate = fieldValue(record, 'summary_date')
  const suretyNumber = fieldValue(record, 'surety_number')
  const bondType = fieldValue(record, 'bond_type')
  const portCode = fieldValue(record, 'port_of_entry')
  const entryDate = fieldValue(record, 'entry_date')
  const modeOfTransport = fieldValue(record, 'mode_of_transport')
  const countryOfOrigin = fieldValue(record, 'country_of_origin')
  const importDate = fieldValue(record, 'import_date')
  const billNumber = fieldValue(record, 'bill_of_lading_awb_number')
  const manufacturerId = fieldValue(record, 'manufacturer_id')
  const exportingCountry = fieldValue(record, 'exporting_country')
  const exportDate = fieldValue(record, 'export_date')
  const itNumber = fieldValue(record, 'it_number')
  const itDate = fieldValue(record, 'it_date')
  const missingDocs = fieldValue(record, 'missing_docs')
  const foreignPort = fieldValue(record, 'foreign_port_of_lading')
  const usPort = fieldValue(record, 'us_port_of_unlading')
  const meltAndPour = fieldValue(record, 'country_of_melt_and_pour')
  const primarySmelt = fieldValue(record, 'primary_country_of_smelt')
  const secondarySmelt = fieldValue(record, 'secondary_country_of_smelt')
  const countryOfCast = fieldValue(record, 'country_of_cast')
  const consigneeNumber = fieldValue(record, 'consignee_number')
  const importerNumber = fieldValue(record, 'importer_number')
  const referenceNumber = fieldValue(record, 'reference_number')
  const importerOfRecord = fieldValue(record, 'importer_of_record')
  const importerAddress = fieldValue(record, 'importer_of_record_address')
  const importerEstateLocation = fieldValue(record, 'importer_of_record_city_location_estate')
  const importerCityIsland = fieldValue(record, 'importer_of_record_city_island')
  const importerStateIsland = fieldValue(record, 'importer_of_record_state_island')
  const importerZipCode = fieldValue(record, 'importer_of_record_zip_code')
  const ultimateAddress = fieldValue(record, 'ultimate_consignee_address')
  const ultimateEstateLocation = fieldValue(record, 'ultimate_consignee_city_location_estate')
  const ultimateCityIsland = fieldValue(record, 'ultimate_consignee_city_island')
  const ultimateStateIsland = fieldValue(record, 'ultimate_consignee_state_island')
  const ultimateZipCode = fieldValue(record, 'ultimate_consignee_zip_code')
  const declarationRole = fieldValue(record, 'declaration_role')
  const declarantName = fieldValue(record, 'declarant_name')
  const declarantTitle = fieldValue(record, 'declarant_title')
  const signatureDate = fieldValue(record, 'signature_date')
  const totalEnteredValue = fieldValue(record, 'total_entered_value')
  const dutyTotal = fieldValue(record, 'duty_total')
  const taxTotal = fieldValue(record, 'tax_total')
  const otherTotal = fieldValue(record, 'other_total')
  const grandTotal = fieldValue(record, 'grand_total')

  if (!TOP_LEVEL_PATTERNS.entryNumber.test(entryNumber)) {
    addIssue(issues, 'entry_number', 'Entry number must use the 3-character filer code, 7-digit entry number, and 1 check digit format.')
  }
  if (!TOP_LEVEL_PATTERNS.entryType.test(entryType)) {
    addIssue(issues, 'entry_type', 'Entry type must use an allowed CBP entry type code.')
  }
  if (!TOP_LEVEL_PATTERNS.suretyNumber.test(suretyNumber)) {
    addIssue(issues, 'surety_number', 'Surety number must be exactly three digits.')
  }
  if (!BOND_TYPES.has(bondType)) {
    addIssue(issues, 'bond_type', 'Bond type must be 0, 8, or 9.')
  }
  if (!TOP_LEVEL_PATTERNS.portCode.test(portCode)) {
    addIssue(issues, 'port_of_entry', 'Port code must be a valid four-digit Schedule D port code.')
  }
  if (!MODE_OF_TRANSPORT.has(modeOfTransport)) {
    addIssue(issues, 'mode_of_transport', 'Mode of transport must use an allowed CBP transport code.')
  }
  if (!TOP_LEVEL_PATTERNS.countryOfOrigin.test(countryOfOrigin)) {
    addIssue(issues, 'country_of_origin', 'Country of origin must use a two-letter ISO code or MULTI.')
  }
  if (!TOP_LEVEL_PATTERNS.billOfLadingOrAirWaybillNumber.test(billNumber)) {
    addIssue(issues, 'bill_of_lading_awb_number', 'B/L or AWB number must be 1 to 16 uppercase letters or numbers.')
  }
  if (!TOP_LEVEL_PATTERNS.manufacturerId.test(manufacturerId)) {
    addIssue(issues, 'manufacturer_id', 'Manufacturer ID must use the constructed MID format or MULTI.')
  }
  if (!TOP_LEVEL_PATTERNS.exportingCountry.test(exportingCountry)) {
    addIssue(issues, 'exporting_country', 'Exporting country must use a two-letter ISO code or MULTI.')
  }
  if (foreignPort && !TOP_LEVEL_PATTERNS.foreignPortOfLading.test(foreignPort)) {
    addIssue(issues, 'foreign_port_of_lading', 'Foreign port of lading must be a five-digit code or MULTI.', 'warning')
  }
  if (usPort && !TOP_LEVEL_PATTERNS.usPortOfUnlading.test(usPort)) {
    addIssue(issues, 'us_port_of_unlading', 'U.S. port of unlading must be a four-digit port code.', 'warning')
  }
  if (meltAndPour && !TOP_LEVEL_PATTERNS.metalCountry.test(meltAndPour)) {
    addIssue(issues, 'country_of_melt_and_pour', 'Country of melt and pour must use a two-letter code, OTH, or N/A.', 'warning')
  }
  if (primarySmelt && !TOP_LEVEL_PATTERNS.metalCountry.test(primarySmelt)) {
    addIssue(issues, 'primary_country_of_smelt', 'Primary country of smelt must use a two-letter code, OTH, or N/A.', 'warning')
  }
  if (secondarySmelt && !TOP_LEVEL_PATTERNS.metalCountry.test(secondarySmelt)) {
    addIssue(issues, 'secondary_country_of_smelt', 'Secondary country of smelt must use a two-letter code, OTH, or N/A.', 'warning')
  }
  if (countryOfCast && !TOP_LEVEL_PATTERNS.metalCountry.test(countryOfCast)) {
    addIssue(issues, 'country_of_cast', 'Country of cast must use a two-letter code, OTH, or N/A.', 'warning')
  }
  if (!TOP_LEVEL_PATTERNS.partyNumber.test(consigneeNumber)) {
    addIssue(issues, 'consignee_number', 'Consignee number must use an allowed EIN, SSN, CBP-assigned, SAME, or consolidated shipment format.')
  }
  if (!TOP_LEVEL_PATTERNS.partyNumber.test(importerNumber)) {
    addIssue(issues, 'importer_number', 'Importer number must use an allowed EIN, SSN, or CBP-assigned format.')
  }
  if (referenceNumber && !TOP_LEVEL_PATTERNS.partyNumber.test(referenceNumber)) {
    addIssue(issues, 'reference_number', 'Reference number must use an allowed EIN, SSN, or CBP-assigned format.', 'warning')
  }
  if (!importerOfRecord) {
    addIssue(issues, 'importer_of_record', 'Importer of record name is required.')
  }
  const importerUsesSplitAddress = hasSplitAddressValues(record, [
    'importer_of_record_address_line_2',
    'importer_of_record_city_location_estate',
    'importer_of_record_city_island',
    'importer_of_record_state_island',
    'importer_of_record_zip_code',
  ])
  const ultimateUsesSplitAddress = hasSplitAddressValues(record, [
    'ultimate_consignee_address_line_2',
    'ultimate_consignee_city_location_estate',
    'ultimate_consignee_city_island',
    'ultimate_consignee_state_island',
    'ultimate_consignee_zip_code',
  ])

  if (
    importerUsesSplitAddress
    && (importerAddress || importerEstateLocation || importerCityIsland || importerStateIsland || importerZipCode)
  ) {
    validateSplitAddressStateAndZip(
      issues,
      {
        stateFieldKey: 'importer_of_record_state_island',
        stateLabel: 'Importer of record state / island',
        zipFieldKey: 'importer_of_record_zip_code',
        zipLabel: 'Importer of record ZIP code',
      },
      record,
    )
  } else {
    validateAddressField(issues, 'importer_of_record_address', importerAddress, 'Importer of record address')
  }

  if (
    ultimateUsesSplitAddress
    && (ultimateAddress || ultimateEstateLocation || ultimateCityIsland || ultimateStateIsland || ultimateZipCode)
  ) {
    validateSplitAddressStateAndZip(
      issues,
      {
        stateFieldKey: 'ultimate_consignee_state_island',
        stateLabel: 'Ultimate consignee state / island',
        zipFieldKey: 'ultimate_consignee_zip_code',
        zipLabel: 'Ultimate consignee ZIP code',
      },
      record,
    )
  }
  if (!DECLARANT_TYPES.has(declarationRole)) {
    addIssue(issues, 'declaration_role', 'Declaration role must identify the importer of record, owner, purchaser, or authorized agent.')
  }
  if (declarantName && !declarantTitle) {
    addIssue(issues, 'declarant_title', 'Declarant title should be completed when a declarant name is provided.', 'warning')
  }
  if (signatureDate && !parseUsDate(signatureDate)) {
    addIssue(issues, 'signature_date', 'Signature date must use MM/DD/YYYY.', 'warning')
  }

  if (!isNonNegativeNumber(totalEnteredValue)) {
    addIssue(issues, 'total_entered_value', 'Total entered value must be a non-negative dollar amount.')
  }
  if (dutyTotal && !isNonNegativeNumber(dutyTotal)) {
    addIssue(issues, 'duty_total', 'Duty must be a non-negative amount.', 'warning')
  }
  if (taxTotal && !isNonNegativeNumber(taxTotal)) {
    addIssue(issues, 'tax_total', 'Tax must be a non-negative amount.', 'warning')
  }
  if (otherTotal && !isNonNegativeNumber(otherTotal)) {
    addIssue(issues, 'other_total', 'Other must be a non-negative amount.', 'warning')
  }
  if (grandTotal && !isNonNegativeNumber(grandTotal)) {
    addIssue(issues, 'grand_total', 'Grand total must be a non-negative amount.', 'warning')
  }

  validateMissingDocuments(issues, missingDocs)

  if (summaryDate && !parseUsDate(summaryDate)) {
    addIssue(issues, 'summary_date', 'Summary date must use MM/DD/YYYY.')
  }
  if (entryDate && !parseUsDate(entryDate)) {
    addIssue(issues, 'entry_date', 'Entry date must use MM/DD/YYYY.')
  }
  if (importDate && !parseUsDate(importDate)) {
    addIssue(issues, 'import_date', 'Import date must use MM/DD/YYYY.')
  }
  if (exportDate && !parseUsDate(exportDate)) {
    addIssue(issues, 'export_date', 'Export date must use MM/DD/YYYY.', 'warning')
  }
  if (itDate && !parseUsDate(itDate)) {
    addIssue(issues, 'it_date', 'I.T. date must use MM/DD/YYYY.', 'warning')
  }

  const parsedSummaryDate = parseUsDate(summaryDate)
  const parsedEntryDate = parseUsDate(entryDate)
  const parsedImportDate = parseUsDate(importDate)
  const parsedItDate = parseUsDate(itDate)

  if (parsedSummaryDate && parsedEntryDate && parsedSummaryDate < parsedEntryDate) {
    addIssue(issues, 'summary_date', 'Summary date must not be earlier than the entry date.', 'warning')
  }
  if ((itNumber && !itDate) || (!itNumber && itDate)) {
    addIssue(issues, 'it_number', 'I.T. number and I.T. date should both be completed when in-bond movement applies.', 'warning')
  }
  if (parsedItDate && parsedImportDate && parsedItDate < parsedImportDate) {
    addIssue(issues, 'it_date', 'I.T. date cannot be earlier than the import date.', 'warning')
  }

  for (const [fieldKey, runtimeState] of Object.entries(fieldStateEngine?.topLevelFields ?? {})) {
    const currentValue = fieldValue(record, fieldKey)
    if (runtimeState.required && !currentValue) {
      addIssue(issues, fieldKey, runtimeState.reason)
    }
    if (runtimeState.showWarningWhenEmpty && !currentValue) {
      addIssue(issues, fieldKey, runtimeState.reason, 'warning')
    }
  }

  for (const lineItem of record.lineItems ?? []) {
    validateLineItem(issues, record, lineItem)
    const runtimeFields = fieldStateEngine?.lineItemFields[lineItem.id] ?? {}
    for (const [fieldKey, runtimeState] of Object.entries(runtimeFields)) {
      const currentValue = lineRuntimeValue(record, lineItem, fieldKey)
      const alreadyHasIssue = issues.some((issue) => issue.fieldKey === `${fieldKey}:${lineItem.id}`)
      if (runtimeState.required && !currentValue) {
        if (!alreadyHasIssue) {
          addIssue(issues, `${fieldKey}:${lineItem.id}`, runtimeState.reason)
        }
      }
      if (runtimeState.showWarningWhenEmpty && !currentValue) {
        if (!alreadyHasIssue) {
          addIssue(issues, `${fieldKey}:${lineItem.id}`, runtimeState.reason, 'warning')
        }
      }
    }
  }

  if (countryOfOrigin === 'MULTI') {
    addIssue(issues, 'country_of_origin', 'MULTI requires line-level country of origin detail. Add this to the line-item model before using MULTI.', 'warning')
  }
  if (exportingCountry === 'MULTI') {
    addIssue(issues, 'exporting_country', 'MULTI requires line-level exporting country detail. Add this to the line-item model before using MULTI.', 'warning')
  }
  if (manufacturerId === 'MULTI') {
    addIssue(issues, 'manufacturer_id', 'MULTI requires line-level manufacturer ID detail. Add this to the line-item model before using MULTI.', 'warning')
  }

  return issues
}
